import { useEffect, useState } from 'react'
import { LuTrash, LuCloudUpload } from 'react-icons/lu'
import { supabase } from '../supabaseClient'
import { random4Digits } from '../utilities/general'
import { AVATAR_FILE_ACCEPT, getAllowedAvatarExtension, isAllowedAvatarFile, isSvgAvatarFile } from '../utilities/avatar'
import { getUserId } from '../lib/workos'
import Avatar from '@mui/material/Avatar'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'

type Props = {
  url: string | null
  size: number
  onUpload: (path: string) => void
  onRemove: () => void
}

export default function AvatarForm({ url, size, onUpload, onRemove }: Props) {
  const [avatarImage, setAvatarImage] = useState<string | null>(url)
  const [uploading, setUploading] = useState<boolean>(false)

  useEffect(() => {
    console.log('AVATAR FORM URL', url)
    if (url?.startsWith('http')) setAvatarImage(url)
    else if (url) downloadAvatar(url)
    else setAvatarImage(null)
  }, [url])

  async function downloadAvatar(downloadUrl: string) {
    try {
      const { data, error } = await supabase.storage.from('avatars').download(downloadUrl)
      if (error) {
        throw error
      }

      if (!isAllowedAvatarFile(downloadUrl, data.type)) {
        if (isSvgAvatarFile(downloadUrl, data.type)) {
          console.warn('Blocked SVG avatar content:', { downloadUrl, mimeType: data.type })
        }
        setAvatarImage(null)
        return
      }

      const image = URL.createObjectURL(data)
      setAvatarImage(image)
    } catch (error) {
      // @ts-expect-error - error is not an instance of Error
      alert('Avatar download failed: ' + error.message)
    }
  }

  async function removeAvatar() {
    if (!url) return

    try {
      const response = await supabase.storage.from('avatars').remove([url])

      console.log('response', response)

      if (response.error) {
        throw response.error
      }

      onRemove()
      console.log('Existing avatar deleted: ', url)
    } catch (error) {
      // @ts-expect-error - error is not an instance of Error
      alert('Avatar remove failed: ' + error.message)
      console.error('Error removing avatar: ', error)
    }
  }

  async function uploadAvatar(event: React.ChangeEvent<HTMLInputElement>) {
    try {
      setUploading(true)

      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.')
      }

      const file = event.target.files[0]
      if (!isAllowedAvatarFile(file.name, file.type)) {
        throw new Error('Unsupported avatar format. Use PNG, JPG, WEBP, GIF, or AVIF.')
      }

      const fileExt = getAllowedAvatarExtension(file.name, file.type)
      if (!fileExt) {
        throw new Error('Unable to determine avatar file extension.')
      }
      const userId = getUserId()
      if (!userId) {
        throw new Error('Unable to upload avatar - not authenticated')
      }
      const fileName = `${userId}-${random4Digits()}.${fileExt}`
      const filePath = `${fileName}`

      await removeAvatar()

      console.log('Uploading avatar: ', { filePath, file })
      const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, file, {
        contentType: file.type || undefined,
      })

      if (uploadError) {
        throw uploadError
      }

      onUpload(filePath)
    } catch (error) {
      // @ts-expect-error - error is not an instance of Error
      alert('Avatar upload failed: ' + error.message)
      console.error('Error uploading avatar: ', error)
    } finally {
      setUploading(false)
    }
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ position: 'relative' }}>
        {url && (
          <IconButton sx={{ position: 'absolute', right: 0 }} size="small" disabled={uploading} onClick={removeAvatar}>
            <LuTrash />
          </IconButton>
        )}
        <Avatar
          src={avatarImage || undefined}
          alt="Avatar"
          sx={{ height: size, width: size, mx: 'auto', mb: 2, bgcolor: 'contrast.minimal' }}
        >
          No Avatar
        </Avatar>
      </Box>
      <Button
        component="label"
        disabled={uploading}
        variant="outlined"
        color="info"
        startIcon={<LuCloudUpload fontSize={18} />}
      >
        {uploading ? 'Uploading...' : 'Upload Avatar'}
        <VisuallyHiddenInput type="file" accept={AVATAR_FILE_ACCEPT} onChange={uploadAvatar} />
      </Button>
    </Stack>
  )
}

// Visually hidden input component
const VisuallyHiddenInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    style={{
      visibility: 'hidden',
      position: 'absolute',
    }}
    {...props}
  />
)
